import React from 'react';

/**
 * Preview pane for a medical opinion attachment.
 *
 * Deliberately source-agnostic: the caller passes an object URL or a data URL, so the same
 * pane serves the approval modal (file fetched from the backend as base64) and the create
 * modal (file still local, previewed via createObjectURL before upload).
 *
 * PDFs render in an iframe and images in an img - the two allowed non-PDF types are JPEG
 * and PNG, both of which the browser renders natively.
 */
interface MedicalOpinionFilePreviewProps {
    // Object URL or data URL. null when nothing is selected yet.
    src: string | null;
    contentType: string | null;
    filename: string | null;
    isLoading?: boolean;
    error?: string | null;
    // Tailwind height class, so each modal can size its own pane.
    heightClass?: string;
}

export const MedicalOpinionFilePreview: React.FC<MedicalOpinionFilePreviewProps> = ({
    src,
    contentType,
    filename,
    isLoading = false,
    error = null,
    heightClass = 'h-64',
}) => {
    const frameClass = `w-full ${heightClass} rounded-lg border-[1px] border-[#B4D9D4] bg-white overflow-hidden flex items-center justify-center`;

    if (isLoading) {
        return (
            <div className={frameClass}>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#366A75]"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={frameClass}>
                <span className="text-sm text-red-600 px-3 text-center">{error}</span>
            </div>
        );
    }

    if (!src) {
        return (
            <div className={frameClass}>
                <span className="text-sm text-gray-500">Select a file to preview</span>
            </div>
        );
    }

    const isPdf = (contentType || '').includes('pdf');

    return (
        <div className={frameClass}>
            {isPdf ? (
                <iframe src={src} title={filename || 'preview'} className="w-full h-full border-0" />
            ) : (
                <img
                    src={src}
                    alt={filename || 'preview'}
                    className="max-w-full max-h-full object-contain"
                />
            )}
        </div>
    );
};
